import { supabase } from './supabase'

const LOG = '[NOIZA][KitchenToggle]'

interface ToggleResult {
  success: boolean
  newValue: boolean
  error?: string
}

/**
 * Robust kitchen pause toggle.
 *
 * Strategy (RLS-safe):
 * 1. Try UPDATE with .select() to get affected rows
 * 2. If update returns 0 rows AND no error → row doesn't exist → INSERT
 * 3. If INSERT also fails (RLS) → try raw RPC or report clearly
 * 4. Re-read DB to confirm value was persisted
 * 5. Never silently fail
 */
export async function toggleKitchenPause(
  restaurantId: string,
  newValue: boolean,
): Promise<ToggleResult> {
  console.log(LOG, '═══════════════════════════════════════')
  console.log(LOG, 'Toggle requested:', {
    restaurantId,
    newValue,
    timestamp: new Date().toISOString(),
  })

  if (!restaurantId) {
    console.error(LOG, 'ABORT: No restaurant_id provided')
    return { success: false, newValue: false, error: 'No restaurant_id' }
  }

  try {
    // ── Step 1: Try UPDATE ──────────────────────────────────
    console.log(
      LOG,
      'Step 1: UPDATE restaurant_settings SET kitchen_paused =',
      newValue,
      'WHERE restaurant_id =',
      restaurantId,
    )

    const {
      data: updateData,
      error: updateError,
      status: updateStatus,
      statusText: updateStatusText,
    } = await supabase
      .from('restaurant_settings')
      .update({ kitchen_paused: newValue })
      .eq('restaurant_id', restaurantId)
      .select('id, restaurant_id, kitchen_paused')

    console.log(LOG, 'UPDATE result:', {
      status: updateStatus,
      statusText: updateStatusText,
      data: updateData,
      error: updateError,
      rowsReturned: updateData?.length ?? 0,
    })

    if (updateError) {
      console.error(LOG, 'UPDATE failed with error:', {
        message: updateError.message,
        details: updateError.details,
        hint: updateError.hint,
        code: updateError.code,
      })

      // If RLS blocks the update, the error code is typically 42501 or the message mentions "policy"
      const isRLS =
        updateError.message?.toLowerCase().includes('policy') ||
        updateError.message?.toLowerCase().includes('permission') ||
        updateError.code === '42501'

      if (isRLS) {
        return {
          success: false,
          newValue: false,
          error: `Permiso denegado por RLS. Verifica que el usuario tiene permisos para actualizar restaurant_settings. (${updateError.message})`,
        }
      }

      return {
        success: false,
        newValue: false,
        error: `Error en UPDATE: ${updateError.message}`,
      }
    }

    // Check if update actually affected a row
    const updateWorked = updateData && updateData.length > 0

    if (updateWorked) {
      console.log(LOG, 'UPDATE succeeded, row updated:', updateData[0])
    } else {
      // ── Step 2: No row exists — try INSERT ──────────────────
      console.warn(
        LOG,
        'UPDATE returned 0 rows — no settings row exists. Attempting INSERT...',
      )

      const {
        data: insertData,
        error: insertError,
        status: insertStatus,
      } = await supabase
        .from('restaurant_settings')
        .insert({
          restaurant_id: restaurantId,
          kitchen_paused: newValue,
        })
        .select('id, restaurant_id, kitchen_paused')

      console.log(LOG, 'INSERT result:', {
        status: insertStatus,
        data: insertData,
        error: insertError,
      })

      if (insertError) {
        console.error(LOG, 'INSERT failed:', {
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint,
          code: insertError.code,
        })

        // If insert also fails, try one more thing: update without .select()
        // Some RLS policies block .select() on update but allow the update itself
        console.warn(LOG, 'Fallback: trying UPDATE without .select()...')

        const { error: fallbackError, status: fallbackStatus } = await supabase
          .from('restaurant_settings')
          .update({ kitchen_paused: newValue })
          .eq('restaurant_id', restaurantId)

        console.log(LOG, 'Fallback UPDATE result:', {
          status: fallbackStatus,
          error: fallbackError,
        })

        if (fallbackError) {
          console.error(
            LOG,
            'ALL attempts failed. Fallback error:',
            fallbackError,
          )
          return {
            success: false,
            newValue: false,
            error: `No se pudo actualizar. UPDATE: ${updateError?.message || 'sin error'}. INSERT: ${insertError.message}. Fallback: ${fallbackError.message}`,
          }
        }

        // Fallback update didn't error — proceed to verify
        console.log(
          LOG,
          'Fallback UPDATE completed without error, proceeding to verify...',
        )
      } else {
        console.log(LOG, 'INSERT succeeded:', insertData)
      }
    }

    // ── Step 3: Re-read from DB to CONFIRM ──────────────────
    console.log(LOG, 'Step 3: Verifying DB value...')

    const { data: verifyData, error: verifyError } = await supabase
      .from('restaurant_settings')
      .select('kitchen_paused')
      .eq('restaurant_id', restaurantId)
      .single()

    console.log(LOG, 'VERIFY result:', {
      data: verifyData,
      error: verifyError,
    })

    if (verifyError) {
      console.error(LOG, 'VERIFY failed:', verifyError)
      // Can't confirm — report as uncertain
      return {
        success: false,
        newValue,
        error: `No se pudo verificar el valor guardado: ${verifyError.message}`,
      }
    }

    const confirmedValue = verifyData.kitchen_paused === true

    if (confirmedValue !== newValue) {
      console.error(
        LOG,
        '❌ VALUE MISMATCH! Expected:',
        newValue,
        'Got:',
        confirmedValue,
      )
      return {
        success: false,
        newValue: confirmedValue,
        error: `El valor no se guardó. Esperado: ${newValue}, en DB: ${confirmedValue}. Posible bloqueo por RLS.`,
      }
    }

    console.log(LOG, '✅ SUCCESS — kitchen_paused =', confirmedValue)
    console.log(LOG, '═══════════════════════════════════════')
    return { success: true, newValue: confirmedValue }
  } catch (err: any) {
    console.error(LOG, '💥 EXCEPTION:', err)
    return {
      success: false,
      newValue: false,
      error: err?.message || 'Error desconocido al actualizar estado de cocina',
    }
  }
}
